<template>
  <div>
    <SkeleLoader
      v-if="isLoading"
      :lines="3"
      imgSize="50px"
      contentIsFullWidth
    />
    <PaymentCardSelect
      v-else-if="isPaymentAvailable"
      :key="orderStore.form.mode"
      :label="$t('PaymentInput.label.paymentMethod')"
      :items="items"
      :selectedPayment="selectedPayment"
      :hideDescription="hideDescription"
      @selectPayment="updateOrderWithPayment"
    />
    <p class="empty-payments" v-else>{{ $t('PaymentInput.emptyState.label') }}</p>
  </div>
</template>

<script setup>
// ====================================
// Packages
// ====================================
import { computed, ref, watch } from "vue";
import { whenever } from "@vueuse/core";
// ====================================
// Storage
// ====================================
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import usePaymentFilters from "@/composables/filters/usePaymentFilters";
import useCreateDefaultsFromApi from "@/composables/api/useCreateDefaultsFromApi";

// ====================================
// Components
// ====================================
import PaymentCardSelect from "@/components/ui/selects/PaymentCardSelect.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const orderStore = useOrderFormStore();
const { getDefaultPayment } = usePaymentFilters();
const { isAnApiOrder } = useCreateDefaultsFromApi();

const props = defineProps({
  items: { type: Array, default: () => [] },
  label: {
    type: String,
    default: "Payment method",
  },
  hideDescription: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  paymentSelected: (payment) => typeof payment === "string",
});

const isLoading = ref(true);
const selectedPayment = ref(null);
const paymentBeenSelectedOnce = ref(false);

const isPaymentAvailable = computed(() => props.items && props.items.length > 0)

const updateOrderWithPayment = (payment) => {
  // prevents from reselecting same payment 2+ from the watcher and triggering api calls
  if (
    selectedPayment.value &&
    payment?.code.toLowerCase() !== selectedPayment.value.toLowerCase()
  ) {
    paymentBeenSelectedOnce.value = false;
  }

  if (!payment?.code || paymentBeenSelectedOnce.value) {
    isLoading.value = false;
    return;
  }

  selectedPayment.value = payment.code.toLowerCase();
  emit("paymentSelected", { payment: selectedPayment.value });
  paymentBeenSelectedOnce.value =
    orderStore.getPaymentObject?.value?.toLowerCase() ===
    selectedPayment.value.toLowerCase();
  orderStore.setActiveModePaymentProvider(selectedPayment.value);
  orderStore.setPspTypeCode(payment.paymentType.toLowerCase());
  orderStore.setPspRequireMobile(payment.require_mobile);
};

const runOnMounted = (paymentOnMount) => {
  if (!paymentOnMount) {
    isLoading.value = false;
    return;
  }
  isLoading.value = false;
  updateOrderWithPayment(paymentOnMount);
};

whenever(
  () =>
    orderStore.getActiveStore.form.coinType.value &&
    props.items.length > 0 &&
    !orderStore.isOrderFromMerchantApi &&
    !isAnApiOrder &&
    orderStore.getActiveStorePaymentData.length > 0,
  () => {
    isLoading.value = true;
    const paymentOnMount = props.items[0];
    if (!orderStore.isOrderFromMerchantApi) {
      runOnMounted(paymentOnMount);
    }
  },
  { deep: true, immediate: true }
);

// Watch for B2B order
watch(
  () =>
    orderStore.isOrderFromMerchantApi &&
    orderStore.filterPaymentsByFiatAndCoin.length &&
    orderStore.getActiveStorePaymentData.length > 0,
  () => {
    isLoading.value = true;
    const paymentOnMount = getDefaultPayment(
      orderStore.getMerchantApiData.payment,
      orderStore.filterPaymentsByFiatAndCoin
    );

    if (orderStore.isOrderFromMerchantApi) {
      runOnMounted(paymentOnMount);
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => props.defaultItem,
  (paymentOnMount) => {
    isLoading.value = true;
    if (paymentOnMount) {
      runOnMounted(paymentOnMount);
    }
  },
  { deep: true }
);
</script>
<style lang="scss">
  .empty-payments {
    color: var(--error-color);
    padding: 2rem 0;
    font-size: 1.5rem;
  }
  .skeleton {
    margin-bottom: $padding-sml;
  }
</style>
